import { ThemeProvider } from '@emotion/react';
import styled from '@emotion/styled';
import Box from '@mui/material/Box';
import { LayoutGroup } from 'framer-motion';
import type { FunctionComponent } from 'react';
import { Fragment, Suspense } from 'react';
import { Outlet } from 'react-router';

import { AiContextProvider } from '~/shared/ai-context';
import { useApplicationContext } from '~/shared/context/use-application-context';
import { useConsentQuery } from '~/shared/query-hooks/consent';
import { Tile } from '~/ui/core/tile/Tile';
import { MAIN_TILE_ID } from '~/ui/domain/tiling-engine/constants';
import { TilingEngine } from '~/ui/domain/tiling-engine/TilingEngine';
import type { TileErrorBoundaryComponent } from '~/ui/domain/tiling-engine/types';
import { theme } from '~/ui/theme/cobrainer-theme';

import { AnnouncementsProvider } from '../announcements/AnnouncementsContext';
import { CHAT_TILE_ID, CHAT_TILE_WIDTH, ChatBody, ChatComposerProvider, TileWithChat, useChatOpen } from '../chat';
import { PageLoadingSpinner } from '../design/components/LoadingSpinner';
import { ConfirmProvider } from '../job-architecture-v2/providers/ConfirmProvider';
import { AutomaticAiContextCollector } from './ai-context/AutomaticAiContextCollector';
import { AppBar } from './app-bar/AppBar';
import { NavigationMenuProvider, useNavigationMenu } from './app-bar/menu-bar-v2/NavigationMenuContext';
import { COLLAPSED_NAV_WIDTH_PX, SideDrawerNavigationV2 } from './app-bar/menu-bar-v2/NavigationMenuV2';
import { shouldRenderOnboarding } from './app-base-conditions';
import { LayoutSlotsProvider, useLayoutSlotRefs } from './common/layout-slots/LayoutSlots';
import { ConsentDialog } from './consent-dialog/ConsentDialog';
import { PageErrorBoundary, TileErrorBoundary } from './error-handling/ErrorBoundaries';
import { AppDrawers } from './pages/app-drawers';
import { route as onboardingRoutes } from './pages/onboarding/constants';
import { OnboardingForwarder } from './pages/onboarding/OnboardingForwarder';
import { ProfilePreview } from './pages/profile/profile-preview';

const tileErrorBoundary: TileErrorBoundaryComponent = ({ children, tileId }) => (
    <TileErrorBoundary tileId={tileId}>{children}</TileErrorBoundary>
);

const LayoutGrid = styled.div<{ readonly shouldOverflow: boolean }>(({ shouldOverflow }) => ({
    backgroundColor: theme.colors.gray[200],
    display: 'grid',
    flex: 1,
    gridTemplateColumns: shouldOverflow ? `auto minmax(calc(100vw - ${COLLAPSED_NAV_WIDTH_PX}px), 1fr)` : 'auto 1fr',
    overflowX: 'hidden',
}));

export type LayoutVariant = 'classic' | 'full-bleed';

const FullBleedContent = styled.div({
    height: '100%',
    minWidth: 0,
    overflow: 'auto',
});

type LayoutProps = {
    readonly hasNavigationMenu?: boolean;
    readonly variant?: LayoutVariant;
};

const APP_BAR_HEIGHT_PX = 60;

type ContentWrapperProps = {
    readonly variant: LayoutVariant;
    readonly tilingEnabled: boolean;
};

const renderOutletContent = (variant: LayoutVariant) =>
    variant === 'full-bleed' ? (
        <Outlet />
    ) : (
        <Tile className="overflow-hidden">
            <div className="overflow-y-auto w-full" style={{ scrollbarWidth: 'thin' }}>
                <Outlet />
            </div>
        </Tile>
    );

const TilingContent: FunctionComponent<{ readonly variant: LayoutVariant }> = ({ variant }) => {
    const { setTopEl } = useLayoutSlotRefs();
    const isOpen = useChatOpen();

    const outletContent = renderOutletContent(variant);
    const outlet = <TileWithChat tileId={MAIN_TILE_ID}>{outletContent}</TileWithChat>;

    return (
        <FullBleedContent className="pr-m">
            <div
                className="grid"
                style={{
                    gridTemplateRows: 'min-content minmax(0, 1fr)',
                    height: `calc(100vh - ${APP_BAR_HEIGHT_PX}px)`,
                }}
            >
                <div ref={setTopEl} />
                <LayoutGroup>
                    <TilingEngine.Surface
                        offsetTop={0}
                        style={{ height: 'auto', minHeight: 0, padding: 12 }}
                        tileErrorBoundary={tileErrorBoundary}
                    >
                        <TilingEngine.Main minWidth="50%">{outlet}</TilingEngine.Main>
                        {isOpen && (
                            <TilingEngine.RightDock
                                id={CHAT_TILE_ID}
                                minWidth={CHAT_TILE_WIDTH}
                                width={CHAT_TILE_WIDTH}
                            >
                                <ThemeProvider theme={theme}>
                                    <ChatBody />
                                </ThemeProvider>
                            </TilingEngine.RightDock>
                        )}
                    </TilingEngine.Surface>
                </LayoutGroup>
            </div>
        </FullBleedContent>
    );
};

const NonTilingContent: FunctionComponent<{ readonly variant: LayoutVariant }> = ({ variant }) => {
    const { setTopEl } = useLayoutSlotRefs();

    return (
        <FullBleedContent className="pr-m">
            <div className="flex flex-col" style={{ height: `calc(100vh - ${APP_BAR_HEIGHT_PX}px)` }}>
                <div ref={setTopEl} />
                <div className="flex" style={{ flex: 1, height: 'auto', minHeight: 0, minWidth: 0 }}>
                    {renderOutletContent(variant)}
                </div>
            </div>
        </FullBleedContent>
    );
};

const ContentWrapper: FunctionComponent<ContentWrapperProps> = ({ variant, tilingEnabled }) =>
    tilingEnabled ? <TilingContent variant={variant} /> : <NonTilingContent variant={variant} />;

const LayoutContent: FunctionComponent<LayoutProps> = ({ hasNavigationMenu = true, variant = 'classic' }) => {
    const { applicationState } = useApplicationContext();
    const { myUserProfile } = applicationState;
    const { data: consentData } = useConsentQuery();
    const { pathname } = applicationState.location;
    const { shouldContentOverflow } = useNavigationMenu();

    if (myUserProfile.isNothing) {
        return <PageLoadingSpinner />;
    }

    if (
        shouldRenderOnboarding({
            consentStatus: consentData?.status,
            onboardingRoutesPath: onboardingRoutes.patterns.onboarding,
            pathname,
            userProfileExists: myUserProfile.value.exists === true,
        })
    ) {
        return (
            <Fragment>
                <OnboardingForwarder />
                <ConsentDialog />
            </Fragment>
        );
    }

    const isAiChatEnabled = applicationState.featureFlagService.isFeatureFlagEnabled('ai-chat');
    const isOnboardingPath = pathname.startsWith(onboardingRoutes.patterns.onboarding);

    const contentCore = (
        <LayoutSlotsProvider>
            <AppBar />
            <Box component="main" flex={1} sx={{ display: 'flex', position: 'relative' }}>
                <PageErrorBoundary>
                    <Suspense fallback={<PageLoadingSpinner />}>
                        <LayoutGrid shouldOverflow={shouldContentOverflow}>
                            {hasNavigationMenu ? <SideDrawerNavigationV2 /> : <span />}
                            <ContentWrapper variant={variant} tilingEnabled={!isOnboardingPath} />
                        </LayoutGrid>
                    </Suspense>
                    <AppDrawers />
                    <ProfilePreview />
                </PageErrorBoundary>
            </Box>
        </LayoutSlotsProvider>
    );

    const wrappedContent = isOnboardingPath ? (
        contentCore
    ) : (
        <TilingEngine.Provider>
            <ChatComposerProvider isEnabled={isAiChatEnabled}>{contentCore}</ChatComposerProvider>
        </TilingEngine.Provider>
    );

    return (
        <Fragment>
            {isAiChatEnabled ? <AutomaticAiContextCollector /> : null}
            <AnnouncementsProvider>
                <ConfirmProvider>{wrappedContent}</ConfirmProvider>
            </AnnouncementsProvider>
        </Fragment>
    );
};

export const Layout: FunctionComponent<LayoutProps> = ({ hasNavigationMenu = true, variant = 'classic' }) => (
    <AiContextProvider>
        <NavigationMenuProvider>
            <LayoutContent hasNavigationMenu={hasNavigationMenu} variant={variant} />
        </NavigationMenuProvider>
    </AiContextProvider>
);
